/**
 * BudgetPlanningDialog — full-screen dialog showing the 12-month projected
 * revenue vs expense for a budget, blending manual recurring items with
 * live system sources (sales orders / bills / payroll / posted journals).
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, TrendingUp, TrendingDown, Sparkles, LineChart } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtMoney } from "@/lib/format";

const SOURCES = [
  { v: "manual", label: "Manual items" },
  { v: "so", label: "Sales orders" },
  { v: "bills", label: "Bills / expenses" },
  { v: "payroll", label: "Payroll" },
  { v: "jl", label: "Journal entries" },
];

const RECURRENCES = [
  { v: "one_time", label: "One-time" },
  { v: "monthly", label: "Monthly" },
  { v: "quarterly", label: "Quarterly" },
  { v: "yearly", label: "Yearly" },
];

export default function BudgetPlanningDialog({ wsId, budget, onClose }) {
  const [enabledSources, setEnabledSources] = useState(() => new Set(["manual", "so", "bills", "payroll"]));
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newItem, setNewItem] = useState({ kind: "revenue", label: "", amount: 0, recurrence: "monthly", start_period: "", end_period: "" });

  const load = async () => {
    if (!budget) return;
    setBusy(true);
    try {
      const src = Array.from(enabledSources).join(",");
      const { data } = await api.get(`/workspaces/${wsId}/budgets/${budget.id}/planning?sources=${src}`);
      setPlan(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [budget?.id, enabledSources]); // eslint-disable-line

  const addItem = async () => {
    if (!newItem.label || !newItem.start_period || !newItem.amount) {
      toast.error("Label, start period, and amount are required"); return;
    }
    try {
      await api.post(`/workspaces/${wsId}/budgets/${budget.id}/manual-items`, {
        ...newItem, amount: Number(newItem.amount),
        end_period: newItem.end_period || null,
      });
      toast.success("Manual item added");
      setNewItem({ kind: "revenue", label: "", amount: 0, recurrence: "monthly", start_period: newItem.start_period, end_period: "" });
      setShowAdd(false);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Add failed")); }
  };

  const delItem = async (iid) => {
    try { await api.delete(`/workspaces/${wsId}/budgets/${budget.id}/manual-items/${iid}`); await load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const toggle = (src) => {
    setEnabledSources((prev) => {
      const n = new Set(prev);
      if (n.has(src)) n.delete(src); else n.add(src);
      return n;
    });
  };

  // Peak values for the mini bar-chart normalisation
  const peak = useMemo(() => {
    if (!plan?.rows) return 1;
    let m = 0;
    plan.rows.forEach((r) => { m = Math.max(m, r.revenue.total, r.expense.total); });
    return m || 1;
  }, [plan]);

  const ccy = budget?.currency || "INR";
  const money = (v) => fmtMoney(v, ccy);

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="budget-planning-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LineChart className="w-5 h-5 text-primary" />
            Planning — {budget?.name} <span className="chip text-[10px] ml-1">{budget?.fiscal_year}</span>
          </DialogTitle>
          <DialogDescription>
            Blends manual items (one-time / monthly / quarterly / yearly) with live system sources to project revenue and expense across the fiscal year.
          </DialogDescription>
        </DialogHeader>

        {/* Source toggles */}
        <div className="flex items-center gap-2 flex-wrap border border-border p-2 bg-muted/30" data-testid="planning-sources">
          <Label className="label-eyebrow text-[10px]">Include sources:</Label>
          {SOURCES.map((s) => (
            <label key={s.v} className="text-xs inline-flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox" checked={enabledSources.has(s.v)}
                onChange={() => toggle(s.v)}
                data-testid={`planning-source-${s.v}`}
              />
              <span className="chip text-[10px]">{s.label}</span>
            </label>
          ))}
        </div>

        {/* Grand summary */}
        {plan && (
          <div className="grid grid-cols-3 gap-3" data-testid="planning-summary">
            <SumTile label="Projected revenue" value={plan.summary.total_revenue} icon={TrendingUp} tone="ok" testid="planning-total-revenue" money={money} />
            <SumTile label="Projected expense" value={plan.summary.total_expense} icon={TrendingDown} tone="danger" testid="planning-total-expense" money={money} />
            <SumTile label="Net" value={plan.summary.net} icon={Sparkles} tone={plan.summary.net >= 0 ? "ok" : "danger"} testid="planning-net" money={money} />
          </div>
        )}

        {/* Manual items editor */}
        <div className="border border-border" data-testid="planning-manual-items">
          <div className="flex items-center gap-2 px-3 py-2 bg-muted/40 border-b border-border">
            <span className="text-sm font-bold">Manual items ({plan?.manual_items?.length || 0})</span>
            <Button size="sm" variant="outline" className="rounded-none h-7 ml-auto" onClick={() => setShowAdd((v) => !v)} data-testid="planning-add-item-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> {showAdd ? "Cancel" : "Add item"}
            </Button>
          </div>
          {showAdd && (
            <div className="p-3 grid grid-cols-6 gap-2 border-b border-border bg-muted/10" data-testid="planning-add-form">
              <Select value={newItem.kind} onValueChange={(v) => setNewItem({ ...newItem, kind: v })}>
                <SelectTrigger className="h-8 rounded-none text-xs" data-testid="planning-new-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="revenue">Revenue</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
              <Input placeholder="Label" value={newItem.label} onChange={(e) => setNewItem({ ...newItem, label: e.target.value })} className="h-8 rounded-none text-xs col-span-2" data-testid="planning-new-label" />
              <Input type="number" placeholder="Amount" value={newItem.amount} onChange={(e) => setNewItem({ ...newItem, amount: e.target.value })} className="h-8 rounded-none text-xs text-right font-mono" data-testid="planning-new-amount" />
              <Select value={newItem.recurrence} onValueChange={(v) => setNewItem({ ...newItem, recurrence: v })}>
                <SelectTrigger className="h-8 rounded-none text-xs" data-testid="planning-new-recurrence"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RECURRENCES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input placeholder="Start (YYYY-MM)" value={newItem.start_period} onChange={(e) => setNewItem({ ...newItem, start_period: e.target.value })} className="h-8 rounded-none text-xs" data-testid="planning-new-start" />
              <Input placeholder="End YYYY-MM (opt)" value={newItem.end_period} onChange={(e) => setNewItem({ ...newItem, end_period: e.target.value })} className="h-8 rounded-none text-xs col-span-5" data-testid="planning-new-end" />
              <Button size="sm" className="rounded-none h-8" onClick={addItem} data-testid="planning-new-save">Save</Button>
            </div>
          )}
          {(plan?.manual_items || []).length === 0 ? (
            <div className="p-4 text-xs text-muted-foreground text-center">No manual items yet — click &ldquo;Add item&rdquo; to add a recurring revenue or expense line.</div>
          ) : (
            <div className="divide-y divide-border">
              {(plan?.manual_items || []).map((it) => (
                <div key={it.id} className="grid grid-cols-8 gap-2 px-3 py-2 items-center text-sm" data-testid={`planning-item-${it.id}`}>
                  <span className={`chip text-[10px] col-span-1 ${it.kind === "revenue" ? "chip-approved" : "chip-rejected"}`}>{it.kind}</span>
                  <span className="col-span-3 truncate font-medium">{it.label}</span>
                  <span className="col-span-1 font-mono text-xs text-right">{money(it.amount)}</span>
                  <span className="col-span-1 chip text-[10px]">{it.recurrence}</span>
                  <span className="col-span-1 text-[10px] text-muted-foreground">
                    {it.start_period}{it.end_period ? ` → ${it.end_period}` : ""}
                  </span>
                  <div className="col-span-1 text-right">
                    <Button size="sm" variant="ghost" className="rounded-none h-7 w-7 p-0 text-red-600" onClick={() => delItem(it.id)} data-testid={`planning-item-del-${it.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Per-period projection */}
        {plan && (
          <div className="border border-border overflow-x-auto" data-testid="planning-grid">
            <div className="grid grid-cols-[110px_1fr_1fr_110px] gap-2 px-3 py-2 bg-muted/30 label-eyebrow text-[10px] min-w-[820px]">
              <div>Period</div>
              <div>Revenue</div>
              <div>Expense</div>
              <div className="text-right">Net</div>
            </div>
            {plan.rows.map((r) => {
              const revW = peak ? Math.min(100, (r.revenue.total / peak) * 100) : 0;
              const expW = peak ? Math.min(100, (r.expense.total / peak) * 100) : 0;
              return (
                <div key={r.period} className="grid grid-cols-[110px_1fr_1fr_110px] gap-2 px-3 py-2 border-t border-border items-center text-xs min-w-[820px]" data-testid={`planning-row-${r.period}`}>
                  <div className="font-mono">{r.period}</div>
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-muted"><div className="h-full bg-emerald-500" style={{ width: `${revW}%` }} /></div>
                      <span className="font-mono w-24 text-right">{money(r.revenue.total)}</span>
                    </div>
                    <SourceBar row={r.revenue} keys={["manual", "so", "jl"]} colors={["#0d9488", "#3b82f6", "#8b5cf6"]} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-muted"><div className="h-full bg-red-500" style={{ width: `${expW}%` }} /></div>
                      <span className="font-mono w-24 text-right">{money(r.expense.total)}</span>
                    </div>
                    <SourceBar row={r.expense} keys={["manual", "bills", "payroll", "jl"]} colors={["#0d9488", "#f97316", "#a855f7", "#8b5cf6"]} />
                  </div>
                  <div className={`text-right font-mono font-bold ${r.net >= 0 ? "text-emerald-600" : "text-red-600"}`}>{money(r.net)}</div>
                </div>
              );
            })}
            <div className="grid grid-cols-[110px_1fr_1fr_110px] gap-2 px-3 py-2 bg-muted/40 border-t-2 border-primary text-xs font-bold min-w-[820px]" data-testid="planning-grand">
              <div>TOTAL</div>
              <div className="font-mono text-emerald-700">{money(plan.summary.total_revenue)}</div>
              <div className="font-mono text-red-700">{money(plan.summary.total_expense)}</div>
              <div className={`text-right font-mono ${plan.summary.net >= 0 ? "text-emerald-700" : "text-red-700"}`}>{money(plan.summary.net)}</div>
            </div>
          </div>
        )}

        {busy && <div className="text-xs text-muted-foreground">Refreshing…</div>}
      </DialogContent>
    </Dialog>
  );
}

function SumTile({ label, value, icon: Icon, tone, testid, money }) {
  const c = tone === "danger" ? "text-red-600" : "text-emerald-600";
  return (
    <div className="border border-border p-3" data-testid={testid}>
      <div className="flex items-center gap-1 label-eyebrow text-[10px]"><Icon className={`w-3 h-3 ${c}`} /> {label}</div>
      <div className={`text-2xl font-black font-mono ${c}`}>{money(value)}</div>
    </div>
  );
}

function SourceBar({ row, keys, colors }) {
  const total = keys.reduce((a, k) => a + (row[k] || 0), 0);
  if (!total) return <div className="h-1 mt-1" />;
  return (
    <div className="flex h-1 mt-1" title={keys.map((k) => `${k}: ${(row[k] || 0).toLocaleString()}`).join(" · ")}>
      {keys.map((k, i) => {
        const w = ((row[k] || 0) / total) * 100;
        if (!w) return null;
        return <div key={k} style={{ width: `${w}%`, background: colors[i] }} className="h-1" />;
      })}
    </div>
  );
}
